import { AdminAuthService } from "@src/application/services/auth/AdminAuthService";
import type { IAdminAuthService } from "@src/domain/interfaces";
import { AdminLoginRepository } from "../repositories/auth/AdminLoginRepository";
import { AdminLoginUseCase } from "@src/application/useCases/auth";
import type { AdminUser } from "@src/domain/entities";
import { SessionStorageAdapter } from "../adapters/sessionStorage";
import { JsonSerializer } from "../adapters/serializers/JsonSerializer";
import { requestContext } from "@ir/core/logger/decorator";
import type { LogContext } from "@src/infrastructure/services/logger/logger.context";

/**
 * Admin Auth Login Service Implementation - Infrastructure layer
 * Implements the admin authentication service
 * Singleton pattern ensures only one instance exists throughout the application
 */
export class AdminAuthLoginService
  extends AdminAuthService
  implements IAdminAuthService
{
  private static instance: AdminAuthLoginService | null = null;
  private currentUser: AdminUser | null = null;
  private loading: boolean = false;

  /**
   * Private constructor to prevent direct instantiation
   * Use getInstance() instead
   */
  private constructor() {
    super();
  }

  /**
   * Get the singleton instance of AdminAuthLoginService
   * @returns The singleton instance
   */
  public static getInstance(): AdminAuthLoginService {
    AdminAuthLoginService.instance ??= new AdminAuthLoginService();
    return AdminAuthLoginService.instance;
  }

  private createLoginRepository(): AdminLoginRepository {
    const storage = new SessionStorageAdapter(new JsonSerializer());
    return new AdminLoginRepository(storage);
  }

  async login(forceRefresh = false): Promise<AdminUser | null> {
    if (this.loading) {
      console.warn("Login already in progress");
    }

    this.loading = true;
    try {
      const adminLoginUseCase = new AdminLoginUseCase(
        this.createLoginRepository(),
      );
      const user = await adminLoginUseCase.execute(forceRefresh);
      const userContext: LogContext = {
        user: {
          username: user?.fullName,
          email: user?.email,
        },
      };
      requestContext.run(userContext, () => {});
      this.currentUser = user;
      return user;
    } catch (error) {
      // Preserve original error message for better debugging
      const errorMessage =
        error instanceof Error ? error.message : "Failed to login Admin User";
      throw new Error(errorMessage);
    } finally {
      this.loading = false;
    }
  }

  async logout(): Promise<void> {
    this.currentUser = null;
    await this.createLoginRepository().clearCache();
  }

  async refreshPermissions(): Promise<AdminUser | null> {
    await this.createLoginRepository().clearCache();
    return this.login(true);
  }

  getUser(): AdminUser | null {
    return this.currentUser ?? null;
  }

  isLoading(): boolean {
    return this.loading;
  }

  async getAccessToken(): Promise<string | null> {
    return this.getUser()?.adminAccessToken ?? null;
  }

  async getRefreshToken(): Promise<string | null> {
    return this.getUser()?.adminRefreshToken ?? null;
  }
}
